'use client';

/**
 * Live insets for the "agency" reference screen.
 * OWNED BY THE INSETS STAGE — see ../index.tsx for the contract.
 *
 * Covers (reference pixels, 1672×941):
 *   - reinforcement toggle [1326,47,84,38] + its two captions (knob translateX 240 ms);
 *   - left panel [60,432,262,222]: mastery bars (85/70/60) or the greyed action counter;
 *   - "что даёт освоение" [60,690,262,148] and "пустые баллы" [1250,683,322,158]:
 *     the active one carries the lime title, the other is dimmed;
 *   - tutor card button [1396,395,176,40] → assistant.
 * The path, the stones, the learner and the tutor portrait stay as painted.
 */

import { useContext, useEffect, useRef, useState, type CSSProperties } from 'react';
import { LivingContext } from '../../context';
import { SCENE_CANVAS, rectStyle, type Bilingual, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './agency.module.css';

const CANVAS = SCENE_CANVAS.agency;
const at = (rect: Rect): CSSProperties => rectStyle(rect, CANVAS);
const stagger = (index: number): CSSProperties => ({ '--i': Math.min(index, 5) } as CSSProperties);

const TOGGLE: Rect = [1326, 47, 84, 38];
const CAPTION_ACTION: Rect = [1134, 48, 184, 36];
const CAPTION_MASTERY: Rect = [1418, 48, 196, 36];
const PROGRESS: Rect = [60, 432, 262, 222];
const GIVES: Rect = [60, 690, 262, 148];
const EMPTY: Rect = [1250, 683, 322, 158];
const TUTOR_BUTTON: Rect = [1396, 395, 176, 40];

const MASTERY_ROWS: readonly { label: Bilingual; value: number }[] = [
  { label: { ru: 'Понимание', en: 'Understanding' }, value: 85 },
  { label: { ru: 'Применение', en: 'Application' }, value: 70 },
  { label: { ru: 'Перенос в новую ситуацию', en: 'Transfer to a new situation' }, value: 60 },
];

const ACTION_ROWS: readonly Bilingual[] = [
  { ru: 'Задание засчитано', en: 'Task counted' },
  { ru: 'Ещё одна попытка', en: 'Another attempt' },
  { ru: 'Баллы начислены', en: 'Points added' },
];

const GIVES_ROWS: readonly Bilingual[] = [
  { ru: 'Уверенность в своих решениях', en: 'Confidence in your own decisions' },
  { ru: 'Свобода выбирать сложные задачи', en: 'Freedom to choose hard tasks' },
  { ru: 'Устойчивый результат надолго', en: 'A result that lasts' },
];

const EMPTY_ROWS: readonly Bilingual[] = [
  { ru: 'Действий ради галочки', en: 'Actions for the tick mark' },
  { ru: 'Временное чувство', en: 'A temporary feeling' },
  { ru: 'Нет уверенности в результате', en: 'No confidence in the result' },
];

function useLiveFlag(key: string): boolean {
  const first = useRef(key);
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!live && key !== first.current) setLive(true);
  }, [key, live]);
  return live;
}

function Badge({ kind }: { kind: 'hex' | 'check' }) {
  if (kind === 'check') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.5l8 4.6v9.8l-8 4.6-8-4.6V7.1z" />
      <path d="M9 12.2l2 2 4-4.4" />
    </svg>
  );
}

export default function AgencyInsets({ lang, state, update, openAssistant, spotlight, onInteraction }: InsetProps) {
  const living = useContext(LivingContext);
  const reduced = living?.reducedMotion ?? false;
  const ru = lang === 'ru';
  const mastery = state.rewardMode === 'mastery';
  const live = useLiveFlag(state.rewardMode);
  const pick = (copy: Bilingual) => (ru ? copy.ru : copy.en);

  const labels = {
    action: ru ? 'награда за действие' : 'reward for action',
    mastery: ru ? 'награда за освоение' : 'reward for mastery',
    actionName: ru ? 'Награда за действие' : 'Reward for action',
    masteryName: ru ? 'Награда за освоение' : 'Reward for mastery',
    toggle: ru ? 'Переключить режим подкрепления' : 'Toggle reinforcement mode',
    progressTitle: mastery ? (ru ? 'ПРОГРЕСС В ОСВОЕНИИ' : 'MASTERY PROGRESS') : (ru ? 'СЧЁТЧИК ДЕЙСТВИЙ' : 'ACTION COUNT'),
    progressNote: ru ? 'Что понято, не видно' : 'What was understood stays invisible',
    gives: ru ? 'ЧТО ДАЁТ ОСВОЕНИЕ' : 'WHAT MASTERY GIVES',
    empty: ru ? 'ПУСТЫЕ БАЛЛЫ' : 'EMPTY POINTS',
    tutor: ru ? 'Задать вопрос' : 'Ask a question',
    tutorName: ru ? 'Задать вопрос тьютору' : 'Ask the tutor',
    group: ru ? 'Режим подкрепления' : 'Reinforcement mode',
  };

  const press = (label: string, action: () => void) => () => {
    onInteraction?.(label);
    action();
  };

  const spot = (id: string) => (spotlight === id ? 'true' : undefined);

  return (
    <div className={styles.root} data-live={live ? 'true' : undefined} data-reduced={reduced ? 'true' : undefined}>
      <div className={styles.modeGroup} role="group" aria-label={labels.group}>
        <button
          type="button"
          className={`${styles.caption} ${styles.captionLeft} ${mastery ? '' : styles.captionActive}`}
          style={at(CAPTION_ACTION)}
          data-hotspot="agency-action"
          data-spotlight={spot('agency-action')}
          aria-pressed={!mastery}
          aria-label={labels.actionName}
          onClick={press(labels.actionName, () => update({ rewardMode: 'action' }))}
        >
          {labels.action}
          {spotlight === 'agency-action' && <i className={styles.pulse} aria-hidden="true" />}
        </button>
        <button
          type="button"
          role="switch"
          className={styles.toggle}
          style={at(TOGGLE)}
          data-hotspot="agency-toggle"
          data-spotlight={spot('agency-toggle')}
          aria-checked={mastery}
          aria-label={labels.toggle}
          onClick={press(labels.toggle, () => update({ rewardMode: mastery ? 'action' : 'mastery' }))}
        >
          <i className={`${styles.knob} ${mastery ? styles.knobOn : ''}`} aria-hidden="true" />
          {spotlight === 'agency-toggle' && <i className={styles.pulse} aria-hidden="true" />}
        </button>
        <button
          type="button"
          className={`${styles.caption} ${mastery ? styles.captionActive : ''}`}
          style={at(CAPTION_MASTERY)}
          data-hotspot="agency-mastery"
          data-spotlight={spot('agency-mastery')}
          aria-pressed={mastery}
          aria-label={labels.masteryName}
          onClick={press(labels.masteryName, () => update({ rewardMode: 'mastery' }))}
        >
          {labels.mastery}
          {spotlight === 'agency-mastery' && <i className={styles.pulse} aria-hidden="true" />}
        </button>
      </div>

      <section className={styles.panel} style={at(PROGRESS)} aria-label={labels.progressTitle}>
        <div key={`body-${state.rewardMode}`} className={styles.body}>
          <p className={`${styles.eyebrow} ${mastery ? styles.eyebrowLime : ''}`} style={stagger(0)}>{labels.progressTitle}</p>
          {mastery ? (
            <ul className={styles.bars}>
              {MASTERY_ROWS.map((row, index) => (
                <li key={row.label.en} style={stagger(index + 1)}>
                  <span className={styles.badge}><Badge kind="hex" /></span>
                  <span className={styles.barLabel}>{pick(row.label)}</span>
                  <span className={styles.barValue}>{row.value}%</span>
                  <span className={styles.track}><i className={styles.fill} style={{ width: `${row.value}%` }} /></span>
                </li>
              ))}
            </ul>
          ) : (
            <>
              <ul className={styles.plain}>
                {ACTION_ROWS.map((row, index) => (
                  <li key={row.en} style={stagger(index + 1)}><i className={styles.hollow} aria-hidden="true" />{pick(row)}</li>
                ))}
              </ul>
              <p className={styles.plainNote} style={stagger(4)}>{labels.progressNote}</p>
            </>
          )}
        </div>
        {live && <i key={`relight-${state.rewardMode}`} className={styles.relight} aria-hidden="true" />}
      </section>

      <section className={`${styles.panel} ${mastery ? '' : styles.dimmed}`} style={at(GIVES)} aria-label={labels.gives}>
        <div key={`body-${state.rewardMode}`} className={styles.body}>
          <p className={`${styles.eyebrow} ${mastery ? styles.eyebrowLime : ''}`} style={stagger(0)}>{labels.gives}</p>
          <ul className={styles.plain}>
            {GIVES_ROWS.map((row, index) => (
              <li key={row.en} style={stagger(index + 1)}><span className={styles.check}><Badge kind="check" /></span>{pick(row)}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`${styles.panel} ${mastery ? styles.dimmed : ''}`} style={at(EMPTY)} aria-label={labels.empty}>
        <div key={`body-${state.rewardMode}`} className={`${styles.body} ${styles.bodyEmpty}`}>
          <p className={`${styles.eyebrow} ${mastery ? '' : styles.eyebrowLime}`} style={stagger(0)}>{labels.empty}</p>
          <ul className={styles.plain}>
            {EMPTY_ROWS.map((row, index) => (
              <li key={row.en} style={stagger(index + 1)}><i className={styles.hollow} aria-hidden="true" />{pick(row)}</li>
            ))}
          </ul>
        </div>
        {live && <i key={`relight-${state.rewardMode}`} className={styles.relight} aria-hidden="true" />}
      </section>

      <button
        type="button"
        className={styles.tutor}
        style={at(TUTOR_BUTTON)}
        data-hotspot="agency-tutor"
        data-spotlight={spot('agency-tutor')}
        aria-label={labels.tutorName}
        onClick={press(labels.tutorName, () => openAssistant(ru ? 'Как помочь ученику почувствовать: «я могу»?' : 'How can the learner feel: I can?'))}
      >
        {labels.tutor}
        {spotlight === 'agency-tutor' && <i className={styles.pulse} aria-hidden="true" />}
      </button>
    </div>
  );
}
